import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { api, getGarageOrderCounts, listGarageOrdersFiltered, OrderListItem } from '../services/api';
import { PartnerDashboardResponse, DashboardOrder, DashboardUpdate } from '../types';
import { AdminActionView } from '../types/offeringsV2';
import { useLanguage } from '../i18n/LanguageContext';
import KpiCards from '../components/dashboard/KpiCards';
import ServicePopularityChart from '../components/dashboard/ServicePopularityChart';
import NewOrdersFeed from '../components/dashboard/NewOrdersFeed';
import InProgressOrders from '../components/dashboard/InProgressOrders';
import LatestUpdates from '../components/dashboard/LatestUpdates';
import GarageStatusBanner from '../components/GarageStatusBanner';

function actionLabel(action: AdminActionView['action']): string {
  switch (action) {
    case 'OFFERING_APPROVED':
    case 'VARIANT_APPROVED':
      return 'Approved';
    case 'OFFERING_REJECTED':
    case 'VARIANT_REJECTED':
      return 'Rejected';
    case 'VARIANT_CHANGES_REQUESTED':
      return 'Changes requested';
    case 'OFFER_APPROVED': return 'Offer approved';
    case 'OFFER_REJECTED': return 'Offer rejected';
    case 'OFFER_SUSPENDED': return 'Offer suspended';
    case 'OFFER_ENDED': return 'Offer ended';
    case 'OFFER_EDITED': return 'Offer edited by Garagat';
    default:
      return action;
  }
}

function actionToUpdate(a: AdminActionView): DashboardUpdate {
  const target = a.offerTitle || a.offeringName || (a.action.startsWith('OFFER_') ? 'offer' : 'service');
  const variant = a.variantLabel ? ` · ${a.variantLabel}` : '';
  const reason = a.reason ? ` — ${a.reason}` : '';
  return {
    timestamp: a.occurredAt,
    message: `${actionLabel(a.action)} — ${target}${variant}${reason}`,
    type: 'ADMIN_ACTION',
  };
}

interface DashboardPageProps {
  auth: { user: { id: string; name: string; email: string } | null; token: string | null };
}

function DashboardPage({ auth }: DashboardPageProps) {
  const { t } = useLanguage();
  const [dashboard, setDashboard] = useState<PartnerDashboardResponse | null>(null);
  const [garageStatus, setGarageStatus] = useState<{ status: string; blockedAt: string | null } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error] = useState<string | null>(null);
  const [adminActions, setAdminActions] = useState<AdminActionView[]>([]);

  // Real order KPIs + NEW-orders feed, from the same partner endpoints the Orders
  // page uses (the backend /me/dashboard KPI block is still placeholder zeros).
  const [orders, setOrders] = useState<DashboardOrder[]>([]);
  const [orderKpi, setOrderKpi] = useState<{
    pendingOrders: number; activeOrders: number; totalOrders: number; completedOrders: number;
  } | null>(null);

  // Ticket #418 — journey-active orders tracker with smart polling (mount + tab
  // focus + 60s while visible). View-only: the mark-complete action stays on the
  // order detail page. Event-driven SSE refresh deliberately deferred (see ticket).
  const [inProgress, setInProgress] = useState<OrderListItem[]>([]);
  const [inProgressLoading, setInProgressLoading] = useState(true);
  const garageIdRef = useRef<string | null>(null);

  const refreshInProgress = useCallback((token: string) => {
    const gid = garageIdRef.current;
    if (!gid) return;
    listGarageOrdersFiltered(gid, 'IN_PROGRESS', false, 0, 10, token)
      .then((page) => setInProgress(page.content ?? []))
      .catch(() => {})
      .finally(() => setInProgressLoading(false));
  }, []);

  useEffect(() => {
    const token = auth.token;
    if (!token) return;
    const tick = () => {
      if (document.visibilityState === 'visible') refreshInProgress(token);
    };
    const interval = window.setInterval(tick, 60_000);
    document.addEventListener('visibilitychange', tick);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [auth.token, refreshInProgress]);

  // Derive the LatestUpdates feed from the admin action feed —
  // the standalone card the previous iteration added has been removed; the
  // existing LatestUpdates card in the right column now shows admin actions.
  const updates = useMemo<DashboardUpdate[]>(() => adminActions.map(actionToUpdate), [adminActions]);

  useEffect(() => {
    if (!auth.token) return;

    api.getMyDashboard(auth.token)
      .then((data) => {
        setDashboard(data);
      })
      .catch(() => {
        // If dashboard endpoint not available, use defaults
        setDashboard({
          garageName: auth.user?.name || t('dashboard.myGarage'),
          commissionRate: null,
          kpi: {
            pendingOrders: 0,
            activeOrders: 0,
            totalOrders: 0,
            completedOrders: 0,
            totalRevenue: 0,
          },
          servicePopularity: [],
        });
      })
      .finally(() => setLoading(false));

    // Garage status banner — uses the same getMyGarage the rest of the partner
    // portal already calls, so no new backend surface is needed. The garage id it
    // returns also keys the real order KPIs and the NEW-orders feed below.
    const token = auth.token;
    api.getMyGarage(token)
      .then((g) => {
        setGarageStatus({ status: g.status, blockedAt: g.blockedAt });
        garageIdRef.current = g.id;
        refreshInProgress(token);

        getGarageOrderCounts(g.id, false, token)
          .then((counts) => setOrderKpi({
            pendingOrders: counts.NEW ?? 0,
            // Journey-active buckets: in service + service-complete awaiting delivery.
            activeOrders: (counts.IN_PROGRESS ?? 0) + (counts.COMPLETED ?? 0),
            totalOrders: counts.ALL ?? 0,
            completedOrders: counts.CLOSED ?? 0,
          }))
          .catch(() => {});

        listGarageOrdersFiltered(g.id, 'NEW', false, 0, 5, token)
          .then((page) => setOrders((page.content ?? []).map((o) => ({
            orderId: o.id,
            type: o.orderNumber ? `Order ${o.orderNumber}` : 'Order',
            serviceName: o.serviceName || '—',
            carModel: o.vehicleMakeModel || '—',
            pickupTime: o.scheduledDate || '',
            pickupLocation: '',
            createdAt: o.createdAt,
          }))))
          .catch(() => {});
      })
      .catch(() => {});

    api.getAdminActionFeed(auth.token, null, 5)
      .then((page) => setAdminActions(page.content ?? []))
      .catch(() => {});
  }, [auth.token, auth.user?.name, t, refreshInProgress]);

  if (loading) {
    return (
      <div className="partner-dashboard">
        <p style={{ color: '#6b7280', textAlign: 'center', padding: '2rem' }}>{t('dashboard.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="partner-dashboard">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  // Live order counts win over the backend dashboard KPI block (placeholder zeros).
  const kpi = orderKpi
    ? { ...orderKpi, totalRevenue: 0 }
    : dashboard?.kpi || {
        pendingOrders: 0,
        activeOrders: 0,
        totalOrders: 0,
        completedOrders: 0,
        totalRevenue: 0,
      };

  const servicePopularity = dashboard?.servicePopularity || [];
  const garageName = dashboard?.garageName || auth.user?.name || t('dashboard.myGarage');

  const commissionRate = dashboard?.commissionRate;

  return (
    <div className="partner-dashboard">
      <h1 className="partner-dashboard-title">{garageName}</h1>

      {garageStatus && (
        <GarageStatusBanner status={garageStatus.status} blockedAt={garageStatus.blockedAt} />
      )}

      {commissionRate !== null && commissionRate !== undefined && (
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.5rem 1rem',
          marginBottom: '1rem',
          borderRadius: '8px',
          backgroundColor: 'var(--card-bg, #fff)',
          border: '1px solid var(--border-color, #e5e7eb)',
          fontSize: '0.875rem',
          color: 'var(--text-secondary, #6b7280)',
        }}>
          <span>{t('dashboard.commissionRate')}:</span>
          <span style={{ fontWeight: 600, color: 'var(--text-primary, #111827)' }}>
            {(commissionRate * 100).toFixed(1)}%
          </span>
        </div>
      )}

      <div className="partner-dashboard-grid">
        {/* Left Column */}
        <div className="partner-dashboard-left">
          <KpiCards kpi={kpi} />
          {/* #418 layout: the two order panels sit beside each other, not stacked.
              CSS grid (not wrapping flex) — the left dashboard column is ~55% wide,
              so a min-width flex basis wrapped straight back to a vertical stack. */}
          <div data-testid="order-panels-row" className="partner-order-panels-row">
            <InProgressOrders orders={inProgress} loading={inProgressLoading} />
            <NewOrdersFeed orders={orders} />
          </div>
        </div>

        {/* Right Column */}
        <div className="partner-dashboard-right">
          <ServicePopularityChart data={servicePopularity} />
          <LatestUpdates updates={updates} />
        </div>
      </div>
    </div>
  );
}

export default DashboardPage;
